/**
 * SalesReports.tsx — Sales / Reports (aggregates page, §6; drill-through §7).
 *
 * One call to /v1/sales/reports (server-computed over the FULL deal set —
 * never a client derivation over the limit-200 list, D4-3). Mockup structure
 * (:749-799): 4 KPI cards, Revenue-by-month bars, Pipeline funnel, Top
 * performers. Trend deltas deliberately OMITTED (R6-1: prior-period pipeline
 * snapshots don't exist; fabricating violates data honesty).
 *
 * Drill-through (§7, R7-1..6): funnel rows, performer rows and revenue bars
 * drill to an IN-PAGE filtered deal table (no URL router exists — drill
 * context is component state). Fetches are server-filtered where the param
 * exists (stage / assigned_to, both live on the deals endpoint); the month
 * drill adds a client-side UTC month cut (reportsDrill.ts). The drill header
 * restates the clicked aggregate, and a shortfall against it is SAID (the
 * list caps at 200; the aggregates don't) — never silently rendered as
 * reconciled. KPI cards stay non-clickable (R7-4: scalars, no list behind
 * them). Bars are pointer-only (R7-3, recharts limitation — known gap).
 * Resting render is pixel-identical to the pre-drill page (R7-5): buttons
 * carry the former row classes; affordances are hover/focus-only.
 *
 * Data honesty: nullable KPIs (cycle before a first win, lead-to-win before
 * a first deal) render as an em dash, never a fake zero. "Revenue" is
 * closed-won deal value, not invoiced revenue — the subtitle says so.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import { SectionTitle, Avatar, DealStageBadge, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { SalesMetric } from './SalesDashboard'
import { useAsync, fetchSalesReports, fetchDeals, fetchClosedDeals, SLUG_TO_STAGE } from '../../api'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { accent, ink, edge, shadow, stageBar, surface } from '../../../../theme/tokens'
import { money } from '../../../../lib/money'
import { type ReportsDrill, filterMonthDeals, drillShortfall } from './reportsDrill'
import type { SalesDeal } from '../../types'

// Takes integer PENCE (private copy, the SalesDashboard/finance precedent).
function fmtMoney(pence: number) {
  const n = pence / 100
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

// "2026-03" → "Mar" — fixed table, no locale/timezone parsing on a bare key.
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function monthLabel(key: string) {
  const m = Number(key.slice(5, 7))
  return MONTH_ABBR[m - 1] ?? key
}

// Funnel rows arrive as the fixed 5-stage spine (open stages + closed_won,
// zero-filled server-side), so the label map and palette slot bind by the
// stage's fixed position — zero counts never drop a row or shift a color.
// Alias of the shared slug→label map (api.ts SLUG_TO_STAGE) — one source,
// no per-page drift; identical values to the retired local copy.
const FUNNEL_LABELS = SLUG_TO_STAGE

// ── The drill view (§7.B): shared filtered deal table, Archive layout ─────────

// The 4 open-stage slugs in funnel order — the composite 'open' drill's fan-out
// ("open" has no server filter; ListDealsQuery is stage/assigned_to only).
const OPEN_SLUGS = ['new_lead', 'qualified', 'proposal_sent', 'negotiation']

function drillTitle(drill: ReportsDrill): { title: string; subtitle: string } {
  switch (drill.kind) {
    case 'stage':
      return { title: drill.label, subtitle: `${drill.count} ${drill.count === 1 ? 'deal' : 'deals'} in this stage` }
    case 'rep':
      return {
        title: drill.rep,
        subtitle: `${drill.openDeals} open · ${money(drill.pipelinePence / 100)} pipeline · ${money(drill.wonThisQuarterPence / 100)} won this quarter (since ${drill.quarterStart})`,
      }
    case 'month':
      return { title: `${monthLabel(drill.month)} ${drill.month.slice(0, 4)}`, subtitle: `${money(drill.wonPence / 100)} closed-won value` }
    case 'open':
      return { title: 'Open deals', subtitle: `${drill.openDeals} open · ${money(drill.pipelinePence / 100)} total pipeline` }
    case 'closed': {
      const closed = drill.wonCount + drill.lostCount
      const pct = closed > 0 ? Math.round((drill.wonCount / closed) * 100) : 0
      return { title: 'Closed deals', subtitle: `${drill.wonCount} won · ${drill.lostCount} lost · ${pct}% win rate` }
    }
    case 'company':
      // Echo the clicked rollup row's five figures verbatim (SB-13 item 2)
      // so the user can see the drill matches the row they clicked.
      return {
        title: drill.name,
        subtitle: `${drill.total} ${drill.total === 1 ? 'deal' : 'deals'} · ${drill.won} won · ${drill.open} open · `
          + `${money(drill.pipelinePence / 100)} open pipeline · ${money(drill.wonPence / 100)} won value`,
      }
  }
}

/** Shared filtered-deal drill (§7.B) — SalesReports' own drills AND the SB-12
 *  dashboard drills render through THIS view; there is deliberately no second
 *  drill component or reconciliation path. `backLabel` names the hosting page. */
export function ReportsDrillView({ drill, onBack, onSelectDeal, backLabel = 'Back to reports', sourceDeals }: {
  drill: ReportsDrill
  onBack: () => void
  onSelectDeal?: (id: string) => void
  backLabel?: string
  /** SB-13: rows supplied BY THE CALLER instead of fetched — the company
   *  drill filters the very list its rollup was computed from, so drill and
   *  rollup are identical by construction. Only the 'company' kind reads
   *  this; a company drill without it renders the (bug-explicit) empty. */
  sourceDeals?: SalesDeal[]
}) {
  const { data, loading, error } = useAsync(
    () =>
      drill.kind === 'stage' ? fetchDeals({ stage: drill.stage })
      : drill.kind === 'rep' ? fetchDeals(drill.employeeId
          ? { assignedEmployeeId: drill.employeeId }   // linked owner
          : { assignedTo: drill.rep })                  // the unlinked bucket
      : drill.kind === 'open' ? Promise.all(OPEN_SLUGS.map(s => fetchDeals({ stage: s }))).then(r => r.flat())
      : drill.kind === 'closed' ? fetchClosedDeals()
      : drill.kind === 'company' ? Promise.resolve(sourceDeals ?? [])
      : fetchDeals({ stage: 'closed_won' }).then(ds => filterMonthDeals(ds, drill.month)),
    [drill],
  )

  const { title, subtitle } = drillTitle(drill)

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const deals = data ?? []
  const shortfall = drillShortfall(drill, deals)

  return (
    <div className="page-enter flex flex-col gap-4">
      <div>
        <button type="button" onClick={onBack}
          className="text-12 text-accent font-medium hover:underline focus:outline-none rounded-control">
          ← {backLabel}
        </button>
      </div>

      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">{title}</div>
            <div className="k-card-sub">{subtitle}</div>
          </div>
        </div>
        {shortfall && (
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            {shortfall.unit === 'deals' && `Showing ${shortfall.shown} of ${shortfall.expected} deals — list calls cap at 200.`}
            {shortfall.unit === 'pence' && `Showing ${money(shortfall.shown / 100)} of ${money(shortfall.expected / 100)} — list calls cap at 200.`}
            {shortfall.unit === 'rep' && `This list doesn't fully reconcile to the row's figures — list calls cap at 200.`}
          </div>
        )}
        {drill.kind === 'company' && (
          // No server aggregate exists for a company group, so no shortfall
          // gate can run — state the shared ceiling honestly instead (the
          // rollup and this drill read the SAME list, so they always agree
          // with each other; both stop at the cap).
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            Grouped client-side from the page's deal list — list calls cap at 200.
          </div>
        )}
        {deals.length === 0 ? (
          drill.kind === 'company'
            // A rollup row exists ONLY because it grouped ≥1 deal, so an
            // empty company drill is a defect worth saying plainly — never
            // dress it up as an ordinary empty (SB-13 item 3).
            ? <EmptyState message="No deals behind this number"
                hint="This company row grouped at least one deal — an empty drill here is a bug, not an empty company." />
            : <EmptyState message="No deals behind this number" hint="Nothing matches this drill right now." />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table sales-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Deal</th>
                  <th className="text-left">Company</th>
                  <th className="text-left">Owner</th>
                  <th className="text-left">Value</th>
                  <th className="text-left">Stage</th>
                  <th className="text-left">Closed</th>
                </tr>
              </thead>
              <tbody>
                {deals.map(d => (
                  <tr key={d.id} className="cursor-pointer hover:bg-surface-row-hover transition-colors"
                    onClick={() => onSelectDeal?.(d.id)}>
                    <td className="text-13 font-semibold text-ink-primary">{d.name}</td>
                    <td>
                      <div className="flex items-center gap-2 text-13 text-ink-body">
                        <Avatar name={d.owner} size={22} />{d.company}
                      </div>
                    </td>
                    <td className="text-13 text-ink-body">{d.owner}</td>
                    <td className="text-13 font-semibold text-ink-primary">{money(d.amount / 100, d.currency)}</td>
                    <td><DealStageBadge stage={d.stage} /></td>
                    <td className="text-12 text-ink-secondary">{d.closedDate || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── The report ────────────────────────────────────────────────────────────────

export function SalesReports({ onSelectDeal }: { onSelectDeal?: (id: string) => void }) {
  const { data, loading, error } = useAsync(fetchSalesReports, [])
  const [drill, setDrill] = useState<ReportsDrill | null>(null)

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  if (drill) return <ReportsDrillView drill={drill} onBack={() => setDrill(null)} onSelectDeal={onSelectDeal} />

  const r = data!
  const maxFunnel = Math.max(1, ...r.funnel.map(f => f.count))
  const revenue = r.revenueByMonth.map(m => ({ ...m, label: monthLabel(m.month) }))

  const KPIS = [
    { label: 'Total pipeline', value: fmtMoney(r.totalPipelinePence), hint: `${r.openDealCount} open deals` },
    // 2.2 (P2-8): the effective-probability weighting; absent on a pre-2.2
    // backend → "—", never a fake 0.
    { label: 'Weighted pipeline', value: r.weightedPipelinePence != null ? fmtMoney(r.weightedPipelinePence) : '—', hint: 'by effective probability' },
    { label: 'Avg. deal size', value: r.openDealCount > 0 ? fmtMoney(r.avgDealSizePence) : '—', hint: 'mean of open deals' },
    { label: 'Avg. sales cycle', value: r.avgSalesCycleDays != null ? `${Math.round(r.avgSalesCycleDays)} days` : '—', hint: 'won deals, created → closed' },
    { label: 'Lead-to-win rate', value: r.leadToWinRatePct != null ? `${Math.round(r.leadToWinRatePct)}%` : '—', hint: 'won ÷ all deals created' },
  ]

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle title="Reports" subtitle="Sales performance overview" />

      {/* 5 KPI cards — .k-metric, value only, no trend badges (R6-1);
          non-clickable (R7-4); weighted pipeline added by 2.2 */}
      <div className="grid grid-cols-5 gap-3">
        {KPIS.map(k => <SalesMetric key={k.label} label={k.label} value={k.value} note={k.hint} />)}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Revenue by month — closed-won value, 6 zero-filled months.
            Bar click drills to that month's won deals — POINTER-ONLY (R7-3:
            recharts bars aren't keyboard-reachable; known, flagged gap). */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Revenue by month</div>
              <div className="k-card-sub">Closed-won value, last 6 months</div>
            </div>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke={edge['row-faint']} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} tickFormatter={v => fmtMoney(v)} />
                <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }} formatter={(v: number) => fmtMoney(v)} cursor={{ fill: surface['row-hover'] }} />
                <Bar dataKey="wonPence" name="Won" fill={accent.DEFAULT} radius={[4, 4, 0, 0]} className="cursor-pointer"
                  onClick={(bar) => {
                    const p = (bar as { payload?: { month?: string; wonPence?: number } }).payload
                    if (p?.month != null && p.wonPence != null) setDrill({ kind: 'month', month: p.month, wonPence: p.wonPence })
                  }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pipeline funnel — per-stage counts, fixed 5-row spine. Rows are
            real buttons (keyboard + focus ring); resting classes unchanged
            (R7-5) so the static render is pixel-identical. */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Pipeline funnel</div>
              <div className="k-card-sub">Deals per stage</div>
            </div>
          </div>
          <div className="p-4 flex flex-col gap-3">
            {r.funnel.map((f, i) => (
              <button key={f.stage} type="button"
                onClick={() => setDrill({ kind: 'stage', stage: f.stage, label: FUNNEL_LABELS[f.stage] ?? f.stage, count: f.count })}
                className="flex items-center gap-3 w-full text-left cursor-pointer rounded-control hover:bg-surface-nav-hover focus:outline-none ">
                <div className="w-24 flex-shrink-0 text-12 text-ink-secondary">{FUNNEL_LABELS[f.stage] ?? f.stage}</div>
                <div className="flex-1 h-2 rounded-full bg-surface-control overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(f.count / maxFunnel) * 100}%`, background: stageBar[i % stageBar.length] }} />
                </div>
                <div className="w-8 flex-shrink-0 text-right text-13 font-semibold text-ink-primary">{f.count}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Top performers — grouped by deal owner, pipeline value desc. Rows
          drill to ALL that rep's deals (R7-2); keyboard-reachable. */}
      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Top performers</div>
            <div className="k-card-sub">{`By pipeline value owned · won this quarter = closed since ${r.quarterStart}`}</div>
          </div>
        </div>
        {/* The E360-2 unlinked bucket, the nonGbpNote idiom: unlinked owners
            are LISTED below like any other — this only says how many deals
            still carry a name rather than a person. Absent when none do. */}
        {r.unlinkedOwnerDeals > 0 && (
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            {r.unlinkedOwnerDeals} {r.unlinkedOwnerDeals === 1 ? 'deal is' : 'deals are'} owned by a name that isn't linked to an employee — {r.unlinkedOwnerDeals === 1 ? 'it groups' : 'they group'} under that name below.
          </div>
        )}
        {r.topPerformers.length === 0 ? (
          <EmptyState message="No performers to rank yet" hint="Deals need an owner before they can be grouped here." />
        ) : (
          <table className="closr-table sales-table w-full">
            <thead>
              <tr>
                <th className="text-left">Rep</th>
                <th className="text-left">Open deals</th>
                <th className="text-left">Pipeline value</th>
                <th className="text-left">Won this quarter</th>
              </tr>
            </thead>
            <tbody>
              {r.topPerformers.map(p => {
                const go = () => setDrill({
                  kind: 'rep', rep: p.rep, employeeId: p.employeeId, openDeals: p.openDeals,
                  pipelinePence: p.pipelinePence, wonThisQuarterPence: p.wonThisQuarterPence,
                  quarterStart: r.quarterStart,
                })
                return (
                  <tr key={p.rep} tabIndex={0} onClick={go}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
                    className="cursor-pointer hover:bg-surface-row-hover transition-colors focus:outline-none ">
                    <td>
                      <div className="flex items-center gap-2 text-13 font-medium text-ink-primary">
                        <Avatar name={p.rep} size={22} />{p.rep}
                      </div>
                    </td>
                    <td className="text-13 text-ink-body">{p.openDeals}</td>
                    <td className="text-13 font-semibold text-ink-primary">{money(p.pipelinePence / 100)}</td>
                    <td className="text-13 text-ink-body">{money(p.wonThisQuarterPence / 100)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
